import type { ConsentManagerConfig } from '@/core/types';

export class FloatingButtonStyles {
  public static getStyles(config: ConsentManagerConfig): string {
    const theme = config.theme || 'light';

    return `
      :host {
        all: initial;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        font-size: 14px;
        line-height: 1.5;
        color: ${this.getColor(theme, 'text')};
        z-index: 2147483646;
        position: fixed;
        bottom: 20px;
        left: 20px;
        opacity: 0;
        visibility: hidden;
        transform: translateY(20px) scale(0.8);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-sizing: border-box;
      }

      :host(.cmp-floating-button--visible) {
        opacity: 1;
        visibility: visible;
        transform: translateY(0) scale(1);
      }

      .cmp-floating-button__container {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .cmp-floating-button__button {
        background: ${this.getColor(theme, 'primary')};
        border: none;
        border-radius: 50%;
        padding: 12px;
        color: white;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 500;
        font-family: inherit;
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        width: 48px;
        height: 48px;
        position: relative;
        overflow: hidden;
      }

      .cmp-floating-button__button:hover,
      .cmp-floating-button__button--hover {
        background: ${this.getColor(theme, 'primaryHover')};
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
      }

      .cmp-floating-button__button:focus {
        outline: 2px solid ${this.getColor(theme, 'primary')};
        outline-offset: 2px;
      }

      .cmp-floating-button__button:active {
        transform: translateY(0);
        transition: transform 0.1s ease;
      }

      .cmp-floating-button__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 20px;
        height: 20px;
      }

      .cmp-floating-button__icon svg {
        width: 20px;
        height: 20px;
        display: block;
      }

      .cmp-floating-button__text {
        display: none;
      }

      /* Responsive design */
      @media (max-width: 768px) {
        :host {
          bottom: 16px;
          left: 16px;
        }

        .cmp-floating-button__button {
          width: 44px;
          height: 44px;
          padding: 10px;
        }

        .cmp-floating-button__icon {
          width: 18px;
          height: 18px;
        }

        .cmp-floating-button__icon svg {
          width: 18px;
          height: 18px;
        }
      }

      /* Accessibility improvements */
      @media (prefers-reduced-motion: reduce) {
        :host,
        .cmp-floating-button__button {
          transition: none;
        }
      }

      @media (prefers-contrast: high) {
        .cmp-floating-button__button {
          border: 2px solid white;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
        }
      }

      /* Pulse animation for first appearance */
      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }

      :host(.cmp-floating-button--pulse) .cmp-floating-button__button {
        animation: pulse 2s ease-in-out 3;
      }

      /* Hover ripple effect */
      .cmp-floating-button__button::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 0;
        height: 0;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 50%;
        transform: translate(-50%, -50%);
        transition: width 0.6s, height 0.6s;
      }

      .cmp-floating-button__button:hover::before {
        width: 100px;
        height: 100px;
      }

      ${config.customCSS || ''}
    `;
  }

  private static getColor(theme: string, colorName: string): string {
    const themes = {
      light: {
        primary: '#2563eb',
        primaryHover: '#1d4ed8',
        text: '#1a1a1a',
      },
      dark: {
        primary: '#3b82f6',
        primaryHover: '#2563eb',
        text: '#f9fafb',
      },
      auto: {
        primary: 'light-dark(#2563eb, #3b82f6)',
        primaryHover: 'light-dark(#1d4ed8, #2563eb)',
        text: 'light-dark(#1a1a1a, #f9fafb)',
      },
    };

    return (
      themes[theme as keyof typeof themes]?.[
        colorName as keyof typeof themes.light
      ] || themes.light[colorName as keyof typeof themes.light]
    );
  }
}
